import React, { useLayoutEffect, useRef } from 'react';
import { dayLabel, timeLabel } from '../../utils/surfaceFormat';
import { isPlaceholderPlace } from '../../utils/placeLabel';

/**
 * BOTTOM SLAB — the Portal's details laid over its video.
 * Design handoff §4 "BOTTOM SLAB overlay".
 *
 * It is the only overlay treatment, and it is built out of a gradient, a black slab, rules and
 * text on purpose: the same thing has to be redrawn server-side in Skia for share cards, and
 * nothing here needs a filter or a blur to survive that trip.
 *
 * No text ever sits directly on the video — it's on the slab (21:1) or over the .85 scrim (12:1).
 */

/** The text is the hero, so the type shrinks as it grows rather than the words being cut. */
export function portalTextSize(text, desktop = false) {
  const n = (text || '').length;
  if (n <= 40) return 34;
  if (n <= 80) return desktop ? 22 : 26;
  if (n <= 110) return 21;
  return 19;
}

export const ROOM_LABEL = {
  public: 'PUBLIC',
  friends: 'FRIENDS',
  close_friends: 'CLOSE FRIENDS',
  select: 'INVITED',
  club: 'CLUB',
};

/** `SAT 8:00–11:00 PM · THE BENCH, KELOWNA` — window first, venue, then city. */
export function whenWhereLine({ startUtc, endUtc, venueName, city }) {
  const parts = [];
  const day = dayLabel(startUtc);
  const from = timeLabel(startUtc);
  const to = endUtc ? timeLabel(endUtc) : null;
  if (day && from) parts.push(to ? `${day} ${from}–${to}` : `${day} ${from}`);
  // "Your location" is the browser's stand-in, not a place anyone can find.
  const place = [venueName, city].filter((p) => p && !isPlaceholderPlace(p)).join(', ');
  if (place) parts.push(place.toUpperCase());
  return parts.join(' · ');
}

/**
 * I'M DOWN (open) or ASK TO JOIN (curated) → REQUESTED while a curated host decides → YOU'RE IN
 * once you're in → VIEW PORTAL when there's nothing to join.
 */
export function actionFor(viewerState, isOwn, mode = 'open') {
  if (isOwn || viewerState === 'host') return { label: 'VIEW PORTAL', done: true };
  if (viewerState === 'accepted') return { label: "YOU'RE IN", done: true, inverted: true };
  if (viewerState === 'applied_pending') return { label: 'REQUESTED', done: true, inverted: true, requested: true };
  if (viewerState === 'not_this_one') return { label: 'VIEW PORTAL', done: true };
  return { label: mode === 'curated' ? 'ASK TO JOIN' : "I'M DOWN", done: false };
}

export default function StoryOverlay({
  portal,
  isOwn = false,
  desktop = false,
  busy = false,
  onAction = null,
  onViewPortal = null,
  replyValue = '',
  onReplyChange = null,
  onReplySend = null,
  replyTo = '',
  /** A text Portal's words are the card itself; only photo and video repeat them here. */
  showText = true,
  /** The slab's height, so a card behind it can fit the space above instead. */
  onHeight = null,
}) {
  const slabRef = useRef(null);
  useLayoutEffect(() => {
    const el = slabRef.current;
    if (!el || !onHeight) return undefined;
    const measure = () => onHeight(el.offsetHeight);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [onHeight]);

  if (!portal) return null;

  const action = actionFor(portal.viewerState, isOwn);
  const room = ROOM_LABEL[portal.room] || 'FRIENDS';
  const when = whenWhereLine(portal);
  const textSize = portalTextSize(portal.text, desktop);

  return (
    <div ref={slabRef} className="pointer-events-none absolute inset-x-0 bottom-0 z-20">
      {/* One gradient, no blur — Skia draws exactly this for the share card. A text Portal's
          card stops at the slab, so there is nothing under it to fade. */}
      {showText && (
        <div
          style={{ height: 110, background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,0.85))' }}
          aria-hidden
        />
      )}

      <div
        className="pointer-events-auto"
        style={{ background: '#000000', borderTop: '2px solid #FFFFFF', padding: desktop ? 14 : 16 }}
      >
        <div className="flex items-center" style={{ gap: 8 }}>
          <span
            className="font-mono font-bold uppercase text-white"
            style={{ border: '2px solid #FFFFFF', padding: '2px 8px', fontSize: 10, letterSpacing: '0.10em' }}
          >
            {room}
          </span>
          <span className="font-mono font-bold uppercase text-white" style={{ fontSize: 10, letterSpacing: '0.10em' }}>
            {portal.acceptedCount} IN · {(portal.mode || 'open').toUpperCase()}
          </span>
        </div>

        {/* Human content: sentence case, never caps. */}
        {showText && portal.text && (
          <p
            className="text-white"
            style={{ marginTop: 10, fontSize: textSize, fontWeight: 400, lineHeight: 1.2 }}
          >
            {portal.text}
          </p>
        )}

        {when && (
          <p
            className="font-mono font-bold uppercase"
            style={{ marginTop: 12, fontSize: 10, letterSpacing: '0.10em', color: 'rgba(255,255,255,0.70)' }}
          >
            {when}
          </p>
        )}

        <button
          type="button"
          disabled={busy}
          onClick={() => (action.done && !action.inverted ? onViewPortal?.() : onAction?.())}
          className="pr-story-action mt-3 w-full font-mono font-semibold uppercase disabled:opacity-60"
          style={{
            background: action.inverted ? '#000000' : '#FFFFFF',
            color: action.inverted ? '#FFFFFF' : '#000000',
            border: '2px solid #FFFFFF',
            fontSize: 14,
            letterSpacing: '0.05em',
            padding: '14px 16px',
            transition: 'background-color 200ms, color 200ms',
          }}
        >
          {busy ? '…' : action.label}
        </button>

        {desktop ? (
          <>
            {!action.done && (
              <button
                type="button"
                onClick={() => onViewPortal?.()}
                className="pr-story-secondary mt-2 w-full font-mono font-bold uppercase text-white"
                style={{ border: '2px solid #FFFFFF', fontSize: 12, letterSpacing: '0.10em', padding: '10px 16px', transition: 'background-color 200ms, color 200ms' }}
              >
                View Portal
              </button>
            )}
            {onReplySend && (
              <div className="mt-3 flex items-center" style={{ gap: 8 }}>
                <input
                  value={replyValue}
                  onChange={(e) => onReplyChange?.(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && replyValue.trim()) onReplySend(); }}
                  placeholder={`Reply to ${replyTo}…`}
                  className="flex-1 bg-transparent text-white placeholder-white/40 focus:outline-none"
                  style={{ border: '2px solid rgba(255,255,255,0.40)', padding: '10px 12px', fontSize: 13 }}
                />
                <button
                  type="button"
                  onClick={() => replyValue.trim() && onReplySend()}
                  className="font-mono font-bold uppercase text-white disabled:opacity-40"
                  disabled={!replyValue.trim()}
                  style={{ fontSize: 11, letterSpacing: '0.10em', padding: '10px 4px' }}
                >
                  Send
                </button>
              </div>
            )}
          </>
        ) : (
          <p
            className="mt-2 text-center font-mono uppercase"
            style={{ fontSize: 10, letterSpacing: '0.05em', color: 'rgba(255,255,255,0.50)' }}
          >
            Swipe up for the full Portal
          </p>
        )}
      </div>

      {/* The phone's home indicator gets its own black strip so the slab never ends mid-gesture. */}
      {!desktop && <div style={{ height: 34, background: '#000000' }} aria-hidden />}
    </div>
  );
}
